<template>
  <div ref="gapRadioGroupRef">
    <template v-if="options?.length">
      <span
        v-for="item in options"
        :key="item[valueKey]"
        class="gap-radio-item"
        :class="[
          activeValue === item[valueKey] ? 'gap-radio-item__active' : '',
          needMarginBottom(item) ? 'mb-24' : ''
        ]"
        :style="{
          width: fixWidth ? fixWidth + 'px' : 'auto'
        }"
        :disabled="item[disabledKey]"
        @click="radioClick(item)">
        <slot :row="item" name="label">
          {{ item[labelKey] }}
        </slot>
      </span>
    </template>
  </div>
</template>

<script setup>
import { toRefs, ref, watch, onMounted, onUnmounted, nextTick } from 'vue'

const emit = defineEmits(['input', 'on-change'])

const props = defineProps({
  options: {
    type: Array,
    default: () => []
  },
  labelKey: {
    type: String,
    default: 'label'
  },
  valueKey: {
    type: String,
    default: 'value'
  },
  disabledKey: {
    type: String,
    default: 'disabled'
  },
  clearable: {
    type: Boolean,
    default: false
  },
  value: [String, Number],
  fixWidth: {
    type: Number,
    default: 150
  }
})

const { options, labelKey, valueKey, value, clearable, disabledKey, fixWidth } = toRefs(props)
const activeValue = ref(1)
const gapRadioGroupRef = ref()
const hasWrap = ref(false)
const lastRowTop = ref(null)
const itemsTopMap = ref(new Map())

const needMarginBottom = item => {
  if (!hasWrap.value) return false
  const currentTop = itemsTopMap.value.get(item[valueKey.value])
  if (currentTop === undefined) {
    return false
  }
  return currentTop !== lastRowTop.value
}

const checkWrap = () => {
  if (!gapRadioGroupRef.value) {
    hasWrap.value = false
    itemsTopMap.value.clear()
    lastRowTop.value = null
    return
  }
  const container = gapRadioGroupRef.value
  const items = container.querySelectorAll('.gap-radio-item')

  if (items.length === 0) {
    hasWrap.value = false
    itemsTopMap.value.clear()
    lastRowTop.value = null
    return
  }

  itemsTopMap.value.clear()

  const topSet = new Set()
  items.forEach((item, index) => {
    const rect = item.getBoundingClientRect()
    const top = Math.round(rect.top * 100) / 100
    const key = options.value[index][valueKey.value]

    itemsTopMap.value.set(key, top)
    topSet.add(top)
  })

  const sortedTops = Array.from(topSet).sort((a, b) => a - b)
  const firstItemTop = sortedTops[0]
  const lastItemTop = sortedTops[sortedTops.length - 1]

  hasWrap.value = Math.abs(lastItemTop - firstItemTop) > 1
  lastRowTop.value = lastItemTop
}

const radioClick = item => {
  if (item[disabledKey.value]) return
  if (activeValue.value === item[valueKey.value] &&!clearable.value) return
  const newValue = activeValue.value === item[valueKey.value] && clearable.value ? '' : item[valueKey.value]
  activeValue.value = newValue
  emit('input', newValue)
  emit('on-change', newValue)
}

watch(() => value.value, n => {
  activeValue.value = n
}, { immediate: true, deep: true })

watch(() => options.value, () => {
  nextTick(() => {
    checkWrap()
  })
})

onMounted(() => {
  nextTick(() => {
    checkWrap()
  })
  window.addEventListener('resize', checkWrap)
})
onUnmounted(() => {
  window.removeEventListener('resize', checkWrap)
})
</script>

<style lang="scss" scoped>
.gap-radio-item{
  display: inline-block;
  border: 1px solid $ws-gray-5;
  border-radius: 4px;
  padding: 4px 14px;
  background-color: #fff;
  color: $ws-gray-8;
  line-height: 20px;
  text-align: center;
  cursor: pointer;
  transition: all .3s cubic-bezier(.645,.045,.355,1);
  &:not(:last-child){
    margin-right: 36px;
  }
  &[disabled] {
    cursor: not-allowed;
    opacity: 0.6;
    &:not(.gap-radio-item__active):hover{
      color: $ws-gray-8;
    }
  }
  &__active{
    background-color: $ws-purple-2;
    border-color: $si-primary-color;
  }
}
</style>